import { useState, type FormEvent } from 'react';
import { ArrowRight, LockKeyhole, Mail, ShieldCheck, UserRound } from 'lucide-react';
import { request } from '../api';
import type { Player } from '../domain/player';
import { Brand } from '../App';

export default function AuthScreen({
  onAuthenticated,
  display = false,
}: {
  onAuthenticated: (player: Player) => void;
  display?: boolean;
}) {
  const [mode, setMode] = useState<'register' | 'login'>(display ? 'login' : 'register');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await request<{ player: Player }>(`auth/${mode}`, {
        email,
        password,
        ...(mode === 'register' ? { username } : {}),
      });
      onAuthenticated(result.player);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <header className="auth-header">
        <Brand link />
        <a href="/">Zur Startseite</a>
      </header>
      <section className="auth-shell">
        <div className="auth-intro">
          <span className="auth-kicker">DEIN LEITSTELLENARBEITSPLATZ</span>
          <h1>
            {display
              ? 'Deine Lagekarte. Auf einem zweiten Bildschirm.'
              : 'Übernimm deinen eigenen Einsatzbereich.'}
          </h1>
          <p>
            {display
              ? 'Melde dich mit demselben Spielerkonto wie am PC an. Einsätze und Fahrzeugbewegungen erscheinen live; die Disposition bleibt am PC.'
              : 'Wähle nach der Registrierung eine deutsche Leitstelle. Die zugehörigen Wachen, Organisationen und Einsatzmittel werden automatisch für deinen Spielstand geladen.'}
          </p>
          <div className="auth-benefits">
            <span>
              <ShieldCheck /> Persönlicher, serverseitig gespeicherter Spielstand
            </span>
            <span>
              <ShieldCheck /> Drei Einsätze zum Dienstbeginn
            </span>
            <span>
              <ShieldCheck /> Credits für Einsätze, Fahrzeuge und Modernisierung
            </span>
          </div>
        </div>
        <form className="auth-card" onSubmit={submit}>
          <div className="auth-tabs" role="tablist" aria-label="Zugang wählen">
            <button
              type="button"
              className={mode === 'register' ? 'active' : ''}
              onClick={() => {
                setMode('register');
                setError('');
              }}
            >
              Registrieren
            </button>
            <button
              type="button"
              className={mode === 'login' ? 'active' : ''}
              onClick={() => {
                setMode('login');
                setError('');
              }}
            >
              Anmelden
            </button>
          </div>
          <div>
            <small>{mode === 'register' ? 'NEUER SPIELER' : 'WILLKOMMEN ZURÜCK'}</small>
            <h2>{mode === 'register' ? 'Spielerkonto anlegen' : 'Dienst übernehmen'}</h2>
          </div>
          {mode === 'register' && (
            <label>
              Spielername
              <span className="auth-input">
                <UserRound />
                <input
                  autoFocus
                  required
                  minLength={3}
                  maxLength={24}
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="z. B. Leitstelle24"
                />
              </span>
            </label>
          )}
          <label>
            E-Mail-Adresse
            <span className="auth-input">
              <Mail />
              <input
                autoFocus={mode === 'login'}
                required
                type="email"
                maxLength={254}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@beispiel.de"
              />
            </span>
          </label>
          <label>
            Passwort
            <span className="auth-input">
              <LockKeyhole />
              <input
                required
                type="password"
                minLength={mode === 'register' ? 8 : 1}
                maxLength={128}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Mindestens 8 Zeichen' : 'Dein Passwort'}
              />
            </span>
          </label>
          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}
          <button className="auth-submit" disabled={busy}>
            {busy ? 'Bitte warten …' : mode === 'register' ? 'Konto erstellen' : 'Anmelden'}
            <ArrowRight />
          </button>
          <p className="auth-note">
            Trainingssimulation für Freizeit und Ausbildung. Kein reales Einsatzleitsystem.
          </p>
        </form>
      </section>
    </main>
  );
}
